import { ConsoleLogEventRes } from "./ConsoleLogEvent.model";
import { CustomLogEventRes } from "./CustomLogEvent.model";
import { NetworkRequestRes, NetworkRequestType } from "./NetworkRequest.model";
import { NotificationEventRes } from "./NotificationEvent.model";
import { ScreenEventRes } from "./ScreenEvent.model";
import { SystemEventRes } from "./SystemEvent.model";
import { UserEventRes } from "./UserEvent.model";

import {
  ConsoleLogEvent,
  CustomLogEvent,
  NetworkRequest,
  NotificationEvent,
  ScreenEvent,
  SystemEvent,
  UserEvent,
} from "./index";

export enum LogType {
  USER_EVENT = "user_event",
  NETWORK_REQUEST = "network_request",
  NOTIFICATION = "notification_event",
  SYSTEM_EVENT = "system_event",
  SCREEN_EVENT = "screen_event",
  CONSOLE_LOG = "console_log",
  CUSTOM_LOG = "custom_log",
}

export type LogUnionType =
  | NetworkRequest
  | UserEvent
  | NotificationEvent
  | SystemEvent
  | ScreenEvent
  | ConsoleLogEvent
  | CustomLogEvent;

export interface Log {
  id: string;
  created: string;
  updated: string;
  pretty_user_events: Array<UserEventRes>;
  pretty_view_controller_history_events: Array<ScreenEventRes>;
  pretty_system_events: Array<SystemEventRes>;
  pretty_network_requests: Array<NetworkRequestRes>;
  pretty_notification_events: Array<NotificationEventRes>;
  pretty_custom_log_events: Array<CustomLogEventRes>;
  pretty_console_log_events: Array<ConsoleLogEventRes>;
  joined_events: Array<LogUnionType>;
  logs_count: number;
}

export const generateActivityHistoryUnion = (log: Log): Array<LogUnionType> => {
  const network: Array<NetworkRequest> = log.pretty_network_requests.map((log) => ({
    ...log,
    instance_type: LogType.NETWORK_REQUEST,
  }));

  const users: Array<UserEvent> = log.pretty_user_events.map((log) => ({ ...log, instance_type: LogType.USER_EVENT }));

  const screenEvents: Array<ScreenEvent> = log.pretty_view_controller_history_events.map((log) => ({
    ...log,
    instance_type: LogType.SCREEN_EVENT,
  }));

  const systemEvents: Array<SystemEvent> = log.pretty_system_events.map((log) => ({
    ...log,
    instance_type: LogType.SYSTEM_EVENT,
  }));

  const notificationEvents: Array<NotificationEvent> = log.pretty_notification_events.map((log) => ({
    ...log,
    instance_type: LogType.NOTIFICATION,
  }));

  const customLogEvents: Array<CustomLogEvent> = log.pretty_custom_log_events.map((log) => ({
    ...log,
    instance_type: LogType.CUSTOM_LOG,
  }));

  const consoleLogEvents: Array<ConsoleLogEvent> = log.pretty_console_log_events.map((log) => ({
    ...log,
    instance_type: LogType.CONSOLE_LOG,
  }));

  return [
    ...network,
    ...users,
    ...screenEvents,
    ...systemEvents,
    ...notificationEvents,
    ...customLogEvents,
    ...consoleLogEvents,
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

export const fillActivityHistoryWithNetworkRequestType = (log: Log, isWebApp: boolean) => {
  log.pretty_network_requests = log.pretty_network_requests.map((networkRequest) => {
    networkRequest.request_type = getNetworkRequestType(networkRequest, isWebApp);
    networkRequest.content_type = getNetworkRequestContentType(networkRequest);
    return networkRequest;
  });
  return log;
};

export const getNetworkRequestType = (networkRequest: NetworkRequestRes, isWebApp: boolean): NetworkRequestType => {
  const url: string = networkRequest.url.toLowerCase();
  const contentType = getNetworkRequestContentType(networkRequest);

  // Preflight
  if (networkRequest.method === "OPTIONS") {
    return "other";
  }

  // Fetch (common API content types)
  if (
    contentType.includes("application/json") ||
    contentType.includes("application/xml") ||
    contentType.includes("text/plain") ||
    contentType.includes("application/x-www-form-urlencoded")
  ) {
    return "fetch";
  }

  // Image
  if (contentType.startsWith("image/") || /\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)(\?|#|$)/.test(url)) {
    return "image";
  }

  // Media (audio/video)
  if (
    contentType.startsWith("audio/") ||
    contentType.startsWith("video/") ||
    /\.(mp3|mp4|wav|ogg|webm|m4a|aac|flac|mov)(\?|#|$)/.test(url)
  ) {
    return "media";
  }

  // WebSocket
  if (url.startsWith("ws://") || url.startsWith("wss://")) {
    return "ws";
  }

  if (isWebApp) {
    // Docs (HTML)
    if (contentType.includes("text/html") || /\.html?(\?|#|$)/.test(url)) {
      return "document";
    }

    // CSS
    if (contentType.includes("text/css") || /\.css(\?|#|$)/.test(url)) {
      return "stylesheet";
    }

    // JS
    if (
      contentType.includes("application/javascript") ||
      contentType.includes("text/javascript") ||
      /\.js(\?|#|$)/.test(url)
    ) {
      return "script";
    }

    // Font
    if (contentType.includes("font/") || /\.(woff|woff2|ttf|otf|eot)(\?|#|$)/.test(url)) {
      return "font";
    }

    // Manifest
    if (contentType.includes("application/manifest+json") || /manifest\.json(\?|#|$)/.test(url)) {
      return "manifest";
    }

    // Wasm
    if (contentType.includes("application/wasm") || /\.wasm(\?|#|$)/.test(url)) {
      return "wasm";
    }
  }

  // Fallback
  return "other";
};

export const getNetworkRequestContentType = (networkRequest: NetworkRequestRes): string => {
  if (networkRequest.method === "OPTIONS") return "preflight";

  const headers = networkRequest.response_headers;
  if (!headers) return "";

  const contentTypeHeader = Object.entries(headers).find(([key]) => key.trim().toLowerCase() === "content-type");
  if (!contentTypeHeader) return "";

  const [, value] = contentTypeHeader;
  return value?.toString()?.split(";")?.[0]?.trim() ?? "";
};
